"use client";

// Librarie
import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

// Component
import Logo from "../Logo/Logo";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import { openConsentPreferences } from "@/lib/cookieConsent";

export default function Footer(props) {
    const t = useTranslations("layout");
    const pathname = usePathname();
    const isHomePage = pathname === "/" || /^\/[a-z]{2}\/?$/.test(pathname);

    return (
        <footer
            className="text-gray-700"
            aria-labelledby="footer-heading"
        >
            {/* Le repère pointait vers un identifiant qui n'existait nulle
                part, donc il n'avait aucun nom accessible. */}
            <h2 id="footer-heading" className="sr-only">
                {t("Footer_heading")}
            </h2>
            <div className="max-w-7xl mx-auto px-4 lg:px-5 py-12 lg:py-16 border-t border-gray-200 dark:border-grayDark-700">
                {/* Main footer content */}
                <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
                    {/* Column 1: Logo + Tagline + Qualiopi */}
                    <div className="lg:col-span-1 space-y-6">
                        <div>
                            <div className="dark:block hidden">
                                <Link
                                    href="/"
                                    className="inline-block focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                                >
                                    <Logo ratio={0.8} white />
                                </Link>
                            </div>
                            <div className="dark:hidden block">
                                <Link
                                    href="/"
                                    className="inline-block focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                                >
                                    <Logo ratio={0.8} />
                                </Link>
                            </div>
                            <p className="mt-3 text-sm text-gray-600 dark:text-grayDark-400 font-medium">
                                For Believers.
                            </p>
                        </div>

                        {/* Qualiopi certification (home only) */}
                        {isHomePage && (
                            <div className="space-y-3">
                                <a
                                    href="https://certifopac.fr/qualiopi/certification/verification/?siren=950731331"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block focus:outline-hidden border focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
                                    aria-label={`Certification Qualiopi - ${t(
                                        "Footer_qualiopi",
                                    )}`}
                                >
                                    <Image
                                        src="/images/site/assets/qualiopi.jpg"
                                        width={200}
                                        height={80}
                                        className="rounded"
                                        alt="Certification Qualiopi"
                                    />
                                </a>
                                <p className="text-xs text-gray-500 dark:text-grayDark-400 leading-relaxed max-w-xs">
                                    {t("Footer_qualiopi_description")}
                                </p>
                                <div className="text-sm">
                                    <a
                                        href="https://certifopac.fr/qualiopi/certification/verification/?siren=950731331"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                        aria-label="Lien vers la vérification de notre certification Qualiopi"
                                    >
                                        {t("Footer_qualiopi")}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Column 2: Products */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_products")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_products")}
                        >
                            <li>
                                <Link
                                    href="/pricing"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Believemy Pro
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/accelerators"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_products_accelerators")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/mentoring"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_products_mentoring")}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Column 3: Enterprise */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_enterprise")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_enterprise")}
                        >
                            <li>
                                <Link
                                    href="/press"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_enterprise_story")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/changelog"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_changelog")}
                                </Link>
                            </li>
                            {/* <li>
                                <Link
                                    href="/pricing"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_enterprise_affiliation")}
                                </Link>
                            </li> */}
                            <li>
                                <Link
                                    href="/support"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_enterprise_help")}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Column 4: Community */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_community")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_community")}
                        >
                            <li>
                                <Link
                                    href="/community"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_community")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="https://believemy.com/go/sZHSnA"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Discord
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/join-instructors"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_becomingInstructor")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/join-mentors"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_becomingMentor")}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Column 5: Accelerators */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_accelerators")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_accelerators")}
                        >
                            <li>
                                <Link
                                    href="/accelerators/rocket"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_accelerators_rocket")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/accelerators/python"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_accelerators_python")}
                                </Link>
                            </li>
                            {/* <li>
                                <a
                                    href="https://belleannee.believemy.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_accelerators_newYear")}
                                </a>
                            </li>
                            <li>
                                <Link
                                    href="/accelerators"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_accelerators_founder")}
                                </Link>
                            </li> */}
                        </ul>
                    </div>

                    {/* Empty column */}
                    <div className="space-y-4 hidden 2xl:block"></div>

                    {/* Column 6: Business */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_business")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_business")}
                        >
                            <li>
                                <Link
                                    href="/teams"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_business_teams")}
                                </Link>
                            </li>
                            {/* <li>
                                <Link
                                    href="/teams"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_business_technological")}
                                </Link>
                            </li> */}
                        </ul>
                    </div>

                    {/* Column 7: Popular courses */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_popularCourses")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_popularCourses")}
                        >
                            <li>
                                <Link
                                    href="/course/formation-python"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Formation Python
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/course/formation-html"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Formation HTML & CSS
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/course/formation-javascript"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Formation JavaScript
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/course/formation-react"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Formation React
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/course/formation-nextjs"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Formation NextJS
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Column 8: Resources */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_resources")}
                        </h3>
                        <ul
                            className="space-y-3"
                            role="list"
                            aria-label={t("Footer_resources")}
                        >
                            <li>
                                <Link
                                    href="/articles"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_articles")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/glossaries"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_glossaries")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/courses"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_courses")}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/workshops"
                                    className="text-sm hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_usefulLinks_workshops")}
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Column 9: Follow Us */}
                    <div className="space-y-4">
                        <h3 className="font-extrabold text-gray-900 dark:text-grayDark-100 font-inter">
                            {t("Footer_followUs")}
                        </h3>

                        {/* Social icons */}
                        <nav
                            className="flex space-x-4"
                            aria-label={t("Footer_followUs")}
                        >
                            <a
                                href="https://www.tiktok.com/@believemyhq"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                aria-label={t("Footer_followUs_tiktok")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                </svg>
                            </a>
                            <a
                                href="https://x.com/BelievemyHQ"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                aria-label={t("Footer_followUs_twitter")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.instagram.com/believemyhq/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                aria-label={t("Footer_followUs_instagram")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.linkedin.com/school/believemy/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                aria-label={t("Footer_followUs_linkedin")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.youtube.com/@believemy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                aria-label={t("Footer_followUs_youtube")}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="h-5 w-5"
                                >
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                            </a>
                        </nav>

                        {/* Contact info */}
                        <div className="space-y-3">
                            <div className="text-sm">
                                <a
                                    href="mailto:hello@believemy.com"
                                    className="text-gray-600 hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    hello@believemy.com
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Copyright bar */}
                <div className="mt-12 border-t border-gray-200 dark:border-grayDark-700 pt-6 text-sm text-gray-500">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div className="flex flex-wrap items-center gap-4">
                            <span className="text-gray-500 dark:text-grayDark-400">
                                © {new Date().getFullYear()} Believemy
                            </span>
                            <div className="flex flex-wrap gap-4">
                                <Link
                                    href="/legal-mentions"
                                    className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_legalMentions")}
                                </Link>
                                <Link
                                    href="/terms-of-sale"
                                    className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_termsOfSale")}
                                </Link>
                                <Link
                                    href="/terms-of-use"
                                    className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_termsOfUse")}
                                </Link>
                                <Link
                                    href="/privacy"
                                    className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_privacy")}
                                </Link>
                                <Link
                                    href="/internal-rules"
                                    className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_internalRules")}
                                </Link>
                                <button
                                    type="button"
                                    onClick={openConsentPreferences}
                                    className="hover:text-gray-900 hover:underline focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    {t("Footer_cookies")}
                                </button>
                            </div>
                        </div>

                        <div className="text-sm">
                            <LanguageSelector />
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
